import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import ViewKanbanOutlinedIcon from '@mui/icons-material/ViewKanbanOutlined';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { GridFilterItem } from '@mui/x-data-grid';
import SubPillTabs from 'components/common/PillTabs/SubPillTabs';
import useCurrentUser from 'hooks/useCurrentUser';
import useIsMobile from 'hooks/useIsMobile';
import { type FC, useEffect, useState } from 'react';
import { type ClientCountBucket, useGetCrmStagesQuery, useGetCrmSubStagesQuery } from 'services/api/clients.api';
import { NAVY, RADIUS_COIN, SHADOW, SURFACE_CONTAINER_LOW, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';
import {
  GAMA_MOBILITY_BUSINESS_UNIT_ID,
  INSUREX_BUSINESS_UNIT_ID,
  POOL_QUEUE_TAB_BUSINESS_UNIT_IDS,
  SALFA_RAC_BUSINESS_UNIT_IDS,
} from 'utils/constants';

export enum ClientQueueTab {
  ASSIGNED = 0,
  PENDING = 1,
  ALL = 2,
  MY_CLIENTS = 3,
  ALL_ASSIGNED = 4,
  PENDING_CRM = 5,
}

interface QueueTabDef {
  value: ClientQueueTab;
  label: string;
  count: number;
  Icon: typeof ScheduleOutlinedIcon;
}

interface ClientQueueTableProps {
  businessUnitId: number;
  selectedTab: ClientQueueTab;
  onSelectedTabChange: (tab: ClientQueueTab) => void;
  /** Called whenever the active tab changes — parent should merge these into its main filterModel. */
  onTabFilterChange: (items: GridFilterItem[]) => void;
  /** Emits the tab-specific count buckets — parent composes them with base/external filters and sends
   * them with the list query, so the badge totals come from the same response as the list. */
  onCountBucketsChange: (buckets: ClientCountBucket[]) => void;
  /** Badge totals keyed by bucket key, resolved from the single list query's `counts`. */
  tabCounts?: Record<string, number>;
}

const ClientQueueTable: FC<ClientQueueTableProps> = ({
  businessUnitId,
  selectedTab,
  onSelectedTabChange,
  onTabFilterChange,
  onCountBucketsChange,
  tabCounts = {},
}) => {
  const currentUser = useCurrentUser();
  const isRACMode = SALFA_RAC_BUSINESS_UNIT_IDS.includes(businessUnitId);
  const isPoolStyle = POOL_QUEUE_TAB_BUSINESS_UNIT_IDS.includes(businessUnitId);
  const isGama = businessUnitId === GAMA_MOBILITY_BUSINESS_UNIT_ID;
  const isMueveSeguro = businessUnitId === INSUREX_BUSINESS_UNIT_ID;
  // "Pendientes CRM" is restricted to admins and business unit admins.
  const canSeeCrmView = isMueveSeguro && !!currentUser && ['admin', 'businessUnitAdmin'].includes(currentUser.role);

  // ── "Pendientes CRM" sub-tabs: Salesforce Etapas (Sale.crmState->>'stage'), Mueve Seguro only ──
  const [selectedCrmStage, setSelectedCrmStage] = useState<string | null>(null);
  const [selectedCrmSubStage, setSelectedCrmSubStage] = useState<string | null>(null);
  const { data: crmStagesData } = useGetCrmStagesQuery({ businessUnitId }, { skip: !canSeeCrmView });
  const crmStages = crmStagesData?.stages ?? [];
  const crmStagesTotal = crmStages.reduce((sum, stage) => sum + stage.count, 0);

  // Subetapas (Sale.crmState->>'subStage') within the selected Etapa.
  const { data: crmSubStagesData } = useGetCrmSubStagesQuery(
    { businessUnitId, stage: selectedCrmStage ?? '' },
    { skip: !canSeeCrmView || !selectedCrmStage || selectedTab !== ClientQueueTab.PENDING_CRM }
  );
  const crmSubStages = crmSubStagesData?.subStages ?? [];

  // ── Auto-reset to first valid tab when entering pool mode ──
  useEffect(() => {
    if (isPoolStyle && selectedTab === ClientQueueTab.ASSIGNED) {
      onSelectedTabChange(ClientQueueTab.PENDING);
    }
  }, [isPoolStyle]);

  // Reset the selected Etapa/Subetapa when the business unit changes (stages differ per BU).
  // biome-ignore lint/correctness/useExhaustiveDependencies: businessUnitId is the intended trigger
  useEffect(() => {
    setSelectedCrmStage(null);
    setSelectedCrmSubStage(null);
  }, [businessUnitId]);

  // Switching Etapa clears any Subetapa narrowing (back to "Todas").
  // biome-ignore lint/correctness/useExhaustiveDependencies: selectedCrmStage is the intended trigger
  useEffect(() => {
    setSelectedCrmSubStage(null);
  }, [selectedCrmStage]);

  // Default to the first available Etapa when entering the "Pendientes CRM" tab.
  useEffect(() => {
    if (selectedTab === ClientQueueTab.PENDING_CRM && !selectedCrmStage && crmStages.length > 0) {
      setSelectedCrmStage(crmStages[0].stage);
    }
  }, [selectedTab, crmStages, selectedCrmStage]);

  // ── Tab-specific filter items ──
  // These carry only each tab's own filter. The parent composes them with base + external (bar/smart)
  // filters before sending them as count buckets, mirroring the list's `mergedFilterModel` (index.tsx),
  // so every badge total resolves against the exact rows its tab would render.
  const myClientsTabItems: GridFilterItem[] = currentUser?.id
    ? [{ field: 'userId', operator: 'equals', value: currentUser.id }]
    : [];
  const pendingTabItems: GridFilterItem[] = [
    { field: 'unreadMessagesCount', operator: 'greaterThan', value: 0 },
    ...(currentUser && ['admin', 'businessUnitAdmin', 'branchManager'].includes(currentUser?.role)
      ? [{ field: 'user', operator: 'isNotEmpty', value: '' }]
      : []),
  ];

  // ── Emit active tab's filters to parent ──
  // Reuses the same *TabItems arrays as the count queries so the list's tab filter can never drift
  // from the badge's. `currentUser` is a dep so this re-emits once the user hydrates (otherwise the
  // MY_CLIENTS / PENDING filters would stay empty and the list would disagree with the badge).
  // biome-ignore lint/correctness/useExhaustiveDependencies: emit only when the tab or its inputs change
  useEffect(() => {
    if (isPoolStyle) {
      if (selectedTab === ClientQueueTab.PENDING) {
        onTabFilterChange([{ field: 'racPending', operator: 'equals', value: true }]);
      } else if (selectedTab === ClientQueueTab.MY_CLIENTS) {
        onTabFilterChange(myClientsTabItems);
      } else if (selectedTab === ClientQueueTab.ALL_ASSIGNED) {
        onTabFilterChange([{ field: 'racAssigned', operator: 'equals', value: true }]);
      } else {
        onTabFilterChange([]);
      }
    } else if (selectedTab === ClientQueueTab.ASSIGNED) {
      onTabFilterChange([{ field: 'lastLeadStatus', operator: 'equals', value: 'ASSIGNED_TO_SALESMAN' }]);
    } else if (selectedTab === ClientQueueTab.PENDING) {
      onTabFilterChange(pendingTabItems);
    } else if (selectedTab === ClientQueueTab.PENDING_CRM) {
      onTabFilterChange([
        ...(selectedCrmStage ? [{ field: 'crmStage', operator: 'equals', value: selectedCrmStage }] : []),
        ...(selectedCrmSubStage ? [{ field: 'crmSubStage', operator: 'equals', value: selectedCrmSubStage }] : []),
      ]);
    } else {
      onTabFilterChange([]);
    }
  }, [
    selectedTab,
    isPoolStyle,
    businessUnitId,
    selectedCrmStage,
    selectedCrmSubStage,
    currentUser?.id,
    currentUser?.role,
  ]);

  // ── Tab badge counts ──
  // Each bucket carries only its tab-specific items; the parent composes them with base + external
  // filters and sends them with the list query, so the badges come from the same response as the rows
  // and can never drift from what the list renders.
  const countBuckets: ClientCountBucket[] = isPoolStyle
    ? [
        { key: 'racPending', items: [{ field: 'racPending', operator: 'equals', value: true }] },
        // Gama uses pool tabs but hides "Asignados a mí" — omit its bucket there.
        ...(isGama ? [] : [{ key: 'myClients', items: myClientsTabItems }]),
        { key: 'racAssigned', items: [{ field: 'racAssigned', operator: 'equals', value: true }] },
        { key: 'all', items: [] },
      ]
    : [
        { key: 'assigned', items: [{ field: 'lastLeadStatus', operator: 'equals', value: 'ASSIGNED_TO_SALESMAN' }] },
        { key: 'pending', items: pendingTabItems },
        { key: 'all', items: [] },
      ];

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-emit only when the bucket set changes
  useEffect(() => {
    onCountBucketsChange(countBuckets);
  }, [isPoolStyle, isGama, businessUnitId, currentUser?.id, currentUser?.role]);

  const { isMobile } = useIsMobile();

  const racTabs: QueueTabDef[] = [
    {
      value: ClientQueueTab.PENDING,
      label: 'Pendientes',
      count: tabCounts.racPending ?? 0,
      Icon: ScheduleOutlinedIcon,
    },
    {
      value: ClientQueueTab.MY_CLIENTS,
      label: 'Asignados a mí',
      count: tabCounts.myClients ?? 0,
      Icon: PersonOutlinedIcon,
    },
    {
      value: ClientQueueTab.ALL_ASSIGNED,
      label: 'Asignados',
      count: tabCounts.racAssigned ?? 0,
      Icon: GroupOutlinedIcon,
    },
    { value: ClientQueueTab.ALL, label: 'Todos', count: tabCounts.all ?? 0, Icon: GroupOutlinedIcon },
  ];

  const standardTabs: QueueTabDef[] = [
    { value: ClientQueueTab.ASSIGNED, label: 'Sin atender', count: tabCounts.assigned ?? 0, Icon: PersonOutlinedIcon },
    { value: ClientQueueTab.PENDING, label: 'Pendientes', count: tabCounts.pending ?? 0, Icon: ScheduleOutlinedIcon },
    { value: ClientQueueTab.ALL, label: 'Todos', count: tabCounts.all ?? 0, Icon: GroupOutlinedIcon },
    ...(canSeeCrmView
      ? [
          {
            value: ClientQueueTab.PENDING_CRM,
            label: 'Pendientes CRM',
            count: crmStagesTotal,
            Icon: ViewKanbanOutlinedIcon,
          },
        ]
      : []),
  ];

  // Gama shares RAC's pool tabs but hides "Asignados a mí" (single executive profile).
  const poolTabs = isGama ? racTabs.filter((tab) => tab.value !== ClientQueueTab.MY_CLIENTS) : racTabs;
  const tabs = isPoolStyle ? poolTabs : standardTabs;
  const showCrmSubTabs = !isRACMode && selectedTab === ClientQueueTab.PENDING_CRM && canSeeCrmView;

  return (
    <Box sx={{ display: 'inline-flex', flexDirection: 'column', gap: 0.75, alignItems: 'flex-start' }}>
      <Box
        sx={{
          display: 'inline-flex',
          gap: 0.5,
          bgcolor: SURFACE_CONTAINER_LOW,
          borderRadius: RADIUS_COIN,
          p: 0.5,
          flexShrink: 0,
        }}
      >
        {tabs.map((tab) => {
          const isActive = selectedTab === tab.value;
          return (
            <Tooltip key={tab.value} title={isMobile ? tab.label : ''} placement="bottom">
              <Box
                onClick={() => onSelectedTabChange(tab.value)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.75,
                  px: { xs: 1, md: 2 },
                  py: 0.75,
                  borderRadius: RADIUS_COIN,
                  cursor: 'pointer',
                  bgcolor: isActive ? SURFACE_CONTAINER_LOWEST : 'transparent',
                  boxShadow: isActive ? SHADOW : 'none',
                  transition: 'all 0.2s ease',
                  '&:hover': { bgcolor: isActive ? SURFACE_CONTAINER_LOWEST : `${NAVY}06` },
                }}
              >
                {isMobile ? (
                  <tab.Icon sx={{ fontSize: '1.1rem', color: isActive ? NAVY : `${NAVY}80` }} />
                ) : (
                  <Typography
                    sx={{
                      fontFamily: 'Jost, sans-serif',
                      fontWeight: isActive ? 700 : 500,
                      fontSize: '0.8rem',
                      color: NAVY,
                      whiteSpace: 'nowrap',
                      userSelect: 'none',
                    }}
                  >
                    {tab.label}
                  </Typography>
                )}
                <Box
                  component="span"
                  sx={{
                    fontFamily: 'Jost, sans-serif',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    color: isActive ? SURFACE_CONTAINER_LOWEST : `${NAVY}60`,
                    bgcolor: isActive ? NAVY : `${NAVY}12`,
                    borderRadius: '10px',
                    minWidth: 20,
                    height: 18,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 0.5,
                    lineHeight: 1,
                  }}
                >
                  {tab.count}
                </Box>
              </Box>
            </Tooltip>
          );
        })}
      </Box>

      {showCrmSubTabs && (
        <Box
          sx={{
            display: 'inline-flex',
            flexWrap: 'wrap',
            gap: 0.5,
            bgcolor: SURFACE_CONTAINER_LOW,
            borderRadius: RADIUS_COIN,
            p: 0.5,
          }}
        >
          {crmStages.length === 0 ? (
            <Typography
              sx={{
                fontFamily: 'Jost, sans-serif',
                fontSize: '0.75rem',
                color: `${NAVY}80`,
                px: 1.5,
                py: 0.75,
                whiteSpace: 'nowrap',
              }}
            >
              Sin etapas con datos
            </Typography>
          ) : (
            crmStages.map((stage) => {
              const isActive = selectedCrmStage === stage.stage;
              return (
                <Box
                  key={stage.stage}
                  onClick={() => setSelectedCrmStage(stage.stage)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: RADIUS_COIN,
                    cursor: 'pointer',
                    bgcolor: isActive ? SURFACE_CONTAINER_LOWEST : 'transparent',
                    boxShadow: isActive ? SHADOW : 'none',
                    transition: 'all 0.2s ease',
                    '&:hover': { bgcolor: isActive ? SURFACE_CONTAINER_LOWEST : `${NAVY}06` },
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Jost, sans-serif',
                      fontWeight: isActive ? 700 : 500,
                      fontSize: '0.75rem',
                      color: NAVY,
                      whiteSpace: 'nowrap',
                      userSelect: 'none',
                      textTransform: 'capitalize',
                    }}
                  >
                    {stage.stage}
                  </Typography>
                  <Box
                    component="span"
                    sx={{
                      fontFamily: 'Jost, sans-serif',
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      color: isActive ? SURFACE_CONTAINER_LOWEST : `${NAVY}60`,
                      bgcolor: isActive ? NAVY : `${NAVY}12`,
                      borderRadius: '10px',
                      minWidth: 18,
                      height: 16,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: 0.5,
                      lineHeight: 1,
                    }}
                  >
                    {stage.count}
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      )}

      {showCrmSubTabs && selectedCrmStage && crmSubStages.length > 0 && (
        <SubPillTabs
          tabs={crmSubStages.map((item) => ({ value: item.subStage, label: item.subStage, count: item.count }))}
          value={selectedCrmSubStage}
          onChange={setSelectedCrmSubStage}
        />
      )}
    </Box>
  );
};

export default ClientQueueTable;
